/**
 * PROD-001 — product lists on calc lines / module nodes.
 *
 * Shape (stored in custom_fields.product_list or node.data.product_list):
 * {
 *   mode: "single" | "multi",
 *   label: string,
 *   ask_on_use: boolean,   // default true when options exist
 *   options: [{
 *     option_key, product_id (nullable until linked), displayTitle,
 *     sales_price, purchase_price, quantity, unit,
 *     default_selected, required, price_source, price_dated_at
 *   }]
 * }
 *
 * Concrete priced lines stay in products / products_data / product_id.
 * The list is the author-time catalogue of alternatives; use-time selection
 * expands into those concrete fields. Options without product_id are drafts
 * until the author links a Promang Product.
 */

export const PRODUCT_LIST_MODES = Object.freeze({
  SINGLE: "single",
  MULTI: "multi",
});

const titleOf = (product) =>
  product?.displayTitle ||
  product?.title ||
  product?.name ||
  product?.productName ||
  product?.product_name ||
  (product?.product_id || product?.id
    ? `Produkt #${product.product_id || product.id}`
    : "");

const newOptionKey = (index = 0) =>
  `opt_${Date.now().toString(36)}_${index}_${Math.random().toString(36).slice(2, 8)}`;

export function isLinkedProductListOption(option) {
  const id = option?.product_id;
  return id != null && id !== "" && Number(id) > 0;
}

export function linkedProductListOptions(list) {
  return normalizeProductList(list).options.filter(isLinkedProductListOption);
}

export function normalizeProductListOption(raw = {}, index = 0) {
  const productIdRaw = raw.product_id ?? raw.id ?? null;
  const productId =
    productIdRaw != null && productIdRaw !== "" && Number(productIdRaw) > 0
      ? Number(productIdRaw) || productIdRaw
      : null;
  const displayTitle = titleOf(raw);
  const optionKey =
    raw.option_key ||
    raw.key ||
    (productId != null ? `p${productId}` : null);

  // Keep linked rows, or draft/manual rows with a title/key
  if (productId == null && !displayTitle && !optionKey) return null;

  return {
    option_key: optionKey || newOptionKey(index),
    product_id: productId,
    displayTitle: displayTitle || (productId != null ? `Produkt #${productId}` : `Linje ${index + 1}`),
    sales_price: Number(raw.sales_price ?? raw.unitPrice ?? raw.unit_price ?? 0) || 0,
    purchase_price:
      Number(raw.purchase_price ?? raw.purchasePrice ?? 0) || 0,
    quantity: Number(raw.quantity ?? 1) || 1,
    unit: raw.unit || null,
    default_selected: raw.default_selected === true || raw.included === true,
    required: raw.required === true,
    price_source:
      raw.price_source || (productId != null ? "product" : "manual"),
    price_dated_at: raw.price_dated_at || null,
    _index: index,
  };
}

export function normalizeProductList(raw) {
  if (!raw || typeof raw !== "object") {
    return emptyProductList();
  }
  const options = (Array.isArray(raw.options) ? raw.options : [])
    .map((opt, index) => normalizeProductListOption(opt, index))
    .filter(Boolean);
  const mode =
    raw.mode === PRODUCT_LIST_MODES.SINGLE
      ? PRODUCT_LIST_MODES.SINGLE
      : PRODUCT_LIST_MODES.MULTI;
  return {
    mode,
    label: raw.label || "",
    ask_on_use: raw.ask_on_use !== false,
    options,
  };
}

export function emptyProductList(overrides = {}) {
  return normalizeProductList({
    mode: PRODUCT_LIST_MODES.SINGLE,
    label: "",
    ask_on_use: true,
    options: [],
    ...overrides,
  });
}

export function createManualProductListOption(overrides = {}, index = 0) {
  return normalizeProductListOption(
    {
      option_key: newOptionKey(index),
      product_id: null,
      displayTitle: overrides.displayTitle || overrides.title || "Ny linje",
      sales_price: 0,
      purchase_price: 0,
      quantity: 1,
      default_selected: false,
      required: false,
      price_source: "manual",
      ...overrides,
    },
    index
  );
}

/**
 * Link (or re-link) an option to a live Promang Product and snapshot prices.
 */
export function linkProductListOptionToProduct(option, product) {
  if (!product) return option;
  const productId = product.id ?? product.product_id;
  if (productId == null || productId === "") return option;
  return applyCatalogPricesToOption(
    {
      ...option,
      product_id: Number(productId) || productId,
      option_key: option?.option_key || `p${productId}`,
    },
    product
  );
}

export function readProductListFromItem(item) {
  const raw =
    item?.product_list ||
    item?.custom_fields?.product_list ||
    item?.custom_fields?.productList ||
    null;
  if (!raw) return null;
  const list = normalizeProductList(raw);
  return list.options.length ? list : null;
}

export function readProductListFromNode(node) {
  if (!node) return null;
  if (node.data?.product_list) {
    const list = normalizeProductList(node.data.product_list);
    return list.options.length ? list : null;
  }
  // productCatalog only when author explicitly enabled ask_on_use
  if (
    node.type === "productCatalog" &&
    node.data?.ask_on_use === true &&
    Array.isArray(node.data?.products) &&
    node.data.products.some((p) => p?.product_id || p?.id || p?.title || p?.displayTitle)
  ) {
    return normalizeProductList({
      mode: PRODUCT_LIST_MODES.MULTI,
      label: node.title || "",
      ask_on_use: true,
      options: node.data.products.map((p) => ({
        ...p,
        default_selected: p.included !== false,
      })),
    });
  }
  return null;
}

export function writeProductListToItem(item, productList) {
  const list = normalizeProductList(productList);
  const custom_fields = { ...(item?.custom_fields || {}) };
  if (!list.options.length) {
    delete custom_fields.product_list;
    const { product_list: _drop, ...rest } = item || {};
    return { ...rest, custom_fields, product_list: undefined };
  }
  custom_fields.product_list = list;
  return { ...item, custom_fields, product_list: list };
}

/** Default selection for wizard / expand when ask_on_use is false or initial. */
export function defaultSelectedProductIds(list) {
  const normalized = normalizeProductList(list);
  const linked = linkedProductListOptions(normalized);
  if (!linked.length) return [];
  if (normalized.mode === PRODUCT_LIST_MODES.SINGLE) {
    const preferred =
      linked.find((o) => o.default_selected) ||
      linked.find((o) => o.required) ||
      linked[0];
    return preferred ? [preferred.product_id] : [];
  }
  const selected = linked
    .filter((o) => o.required || o.default_selected)
    .map((o) => o.product_id);
  if (selected.length) return selected;
  return [];
}

export function optionToConcreteProduct(option) {
  return {
    id: option.product_id,
    product_id: option.product_id,
    displayTitle: option.displayTitle,
    sales_price: option.sales_price,
    purchase_price: option.purchase_price,
    quantity: option.quantity || 1,
    discount: 0,
    surcharge: 0,
    divide_by_quantity: false,
    show_image: false,
    show_url: false,
    unit: option.unit || null,
  };
}

/**
 * Expand a product_list selection onto a calculation item.
 * - single → sets product_id / product / prices (product line) OR replaces products
 * - multi  → sets products (products_data) from selected options
 * Unlinked options are ignored.
 */
export function expandProductListOntoItem(item, selectedIds, { clearList = false } = {}) {
  const list = readProductListFromItem(item) || normalizeProductList(item?.product_list);
  const linked = linkedProductListOptions(list);
  if (!linked.length) return item;

  const idSet = new Set(
    (Array.isArray(selectedIds) ? selectedIds : [selectedIds])
      .filter((id) => id != null && id !== "")
      .map((id) => String(id))
  );

  // Always keep required linked options
  linked.forEach((opt) => {
    if (opt.required) idSet.add(String(opt.product_id));
  });

  const chosen = linked.filter((opt) => idSet.has(String(opt.product_id)));
  let next = { ...item };

  if (list.mode === PRODUCT_LIST_MODES.SINGLE) {
    const pick = chosen[0] || linked[0];
    if (pick) {
      next.product_id = pick.product_id;
      next.product = {
        id: pick.product_id,
        displayTitle: pick.displayTitle,
        sales_price: pick.sales_price,
        purchase_price: pick.purchase_price,
      };
      next.sales_price = pick.sales_price;
      next.purchase_price = pick.purchase_price;
      if (pick.unit?.id) {
        next.unit_id = pick.unit.id;
        next.unit = pick.unit;
      }
      if (!next.quantity || Number(next.quantity) <= 0) {
        next.quantity = pick.quantity || 1;
      }
      if (!next.description) {
        next.description = pick.displayTitle;
      }
    }
  } else {
    const concrete = chosen.map(optionToConcreteProduct);
    // Merge: keep non-list products, replace list-sourced ones by id
    const existing = Array.isArray(next.products) ? next.products : [];
    const listIds = new Set(linked.map((o) => String(o.product_id)));
    const kept = existing.filter((p) => !listIds.has(String(p.id || p.product_id)));
    next.products = [...kept, ...concrete];
  }

  if (clearList) {
    next = writeProductListToItem(next, emptyProductList());
  } else {
    next = writeProductListToItem(next, list);
  }
  return next;
}

/**
 * Apply selection onto a module productCatalog node (included flags).
 */
export function expandProductListOntoNode(node, selectedIds) {
  const list = readProductListFromNode(node);
  if (!list) return node;
  const linked = linkedProductListOptions(list);
  const idSet = new Set(
    (Array.isArray(selectedIds) ? selectedIds : [selectedIds])
      .filter((id) => id != null && id !== "")
      .map((id) => String(id))
  );
  linked.forEach((opt) => {
    if (opt.required) idSet.add(String(opt.product_id));
  });

  const products = (node.data?.products || []).map((p) => {
    const pid = String(p.product_id ?? p.id ?? "");
    if (!pid) return { ...p, included: false };
    return { ...p, included: idSet.has(pid) };
  });

  return {
    ...node,
    data: {
      ...(node.data || {}),
      products,
      product_list: {
        ...list,
        options: list.options.map((o) => ({
          ...o,
          default_selected: isLinkedProductListOption(o)
            ? idSet.has(String(o.product_id))
            : false,
        })),
      },
    },
  };
}

/**
 * Author product_list onto a productCatalog node and keep data.products in sync
 * for EvaluateModuleGraph (unitPrice × quantity).
 */
export function writeProductListToNode(node, productList) {
  const list = normalizeProductList(productList);
  const products = list.options.map((opt, index) => ({
    id: opt.product_id || opt.option_key || `opt-${index}`,
    product_id: opt.product_id,
    title: opt.displayTitle,
    displayTitle: opt.displayTitle,
    quantity: opt.quantity || 1,
    unitPrice: opt.sales_price || 0,
    sales_price: opt.sales_price || 0,
    purchase_price: opt.purchase_price || 0,
    unit: opt.unit || null,
    included: opt.default_selected !== false,
    price_source: opt.price_source || null,
    price_dated_at: opt.price_dated_at || null,
    option_key: opt.option_key,
    required: opt.required === true,
  }));

  return {
    ...node,
    data: {
      ...(node.data || {}),
      ask_on_use: list.ask_on_use !== false,
      product_list: list.options.length ? list : undefined,
      products,
    },
  };
}

/** Read author list for module builder (product_list or legacy products[]). */
export function authorProductListFromNode(node) {
  if (!node) return emptyProductList();
  if (node.data?.product_list) {
    return normalizeProductList({
      ...node.data.product_list,
      ask_on_use:
        node.data.product_list.ask_on_use !== false &&
        node.data.ask_on_use !== false,
      label: node.data.product_list.label || node.title || "",
    });
  }
  if (Array.isArray(node.data?.products) && node.data.products.length) {
    return normalizeProductList({
      mode: PRODUCT_LIST_MODES.MULTI,
      label: node.title || "",
      ask_on_use: node.data.ask_on_use === true,
      options: node.data.products.map((p) => ({
        ...p,
        default_selected: p.included !== false,
      })),
    });
  }
  return emptyProductList({
    label: node.title || "",
    ask_on_use: node.data?.ask_on_use === true,
  });
}

/** Refresh option prices from a live product record (catalog sync). */
export function applyCatalogPricesToOption(option, product) {
  if (!product) return option;
  const productId = product.id ?? product.product_id ?? option.product_id;
  return {
    ...option,
    product_id: productId != null && productId !== "" ? Number(productId) || productId : option.product_id,
    displayTitle: titleOf(product) || option.displayTitle,
    sales_price: Number(product.sales_price ?? option.sales_price) || 0,
    purchase_price: Number(product.purchase_price ?? option.purchase_price) || 0,
    unit: product.unit || option.unit || null,
    price_source: "product",
    price_dated_at: new Date().toISOString(),
  };
}

export function applyCatalogPricesToList(list, productsById) {
  const normalized = normalizeProductList(list);
  return {
    ...normalized,
    options: normalized.options.map((opt) => {
      if (!isLinkedProductListOption(opt)) return opt;
      const live = productsById?.[opt.product_id] || productsById?.[String(opt.product_id)];
      return live ? applyCatalogPricesToOption(opt, live) : opt;
    }),
  };
}
